// Figma "Untitled" > Lufthansa, section 75:7142, frames in screen order:
// 75:7143 (cover), 77:8700, 78:8748, 79:8782, 80:8902; phones also use the
// landscape photos 80:8946 and 80:8948 (Tiago, 5 Nov: under the videos of
// modules 2 and 3, in place of the portraits).
// Phones (Tiago, 5 Nov): the cover without the photo, the video at its own
// ratio and the card taking what's left; module 4 the two cards one above the
// other; module 5's chat flow on a screen of its own after module 5.
import type { Case, Img } from "../types"

const own = (n: string, w: number, h: number, pos?: string): Img => {
    const src = `/work/lufthansa/${n}`
    return { id: src, src, w, h, widths: [...[800, 1600].filter((x) => x < w), w], pos }
}
// the videos' first frames (made by the encoder, at the sizes it made)
const COVER = own("c-cover", 1920, 1080)
const SEAT = own("c-seat", 1920, 1080)
const CHAT = own("c-chat", 1920, 1080)
const WINDOW = own("c-window", 1920, 1080)
// (the files are named the other way round: Module5_right.mp4 is the flow
// designed in module 4, Module4_right.mp4 the one in module 5)
const FLOW_A = own("c-flow-a", 1500, 2000)
const FLOW_B = own("c-flow-b", 1500, 2000)
const TALKING = own("c-talking", 690, 920)
const THINKING = own("c-thinking", 690, 920)

export const lufthansa: Case = {
    name: "Lufthansa",
    theme: {
        page: "#a9c2ed",
        gradient: "linear-gradient(180deg, #a9c2ed 0%, #eaf1ff 100%)",
        stops: [
            [169, 194, 237, 0],
            [234, 241, 255, 1],
        ],
        fill: "#05164d",
        line: "#05164d",
        tone: "light",
        title: "#05164d",
        body: "#05164d",
    },
    modules: [
        // 1: tag 220 | photo 602 x 347 over the video 882 x 496 | card 443 x 400 at the bottom
        {
            type: "grid",
            columns: [
                {
                    w: 220,
                    cells: [
                        {
                            h: 220,
                            tag: {
                                logo: "/work/lufthansa/logo.svg",
                                // node 75:7172 "path22", 147 x 25; top left as the other tags (Tiago, 5 Nov; Figma had it at 20, 46)
                                logoBox: [20, 20, 147, 25],
                                text: "DEUTSCHE LUFTHANSA AG\nAIRLINE GROUP\nFOUNDED 1926\n\nFULL SERVICE",
                            },
                        },
                        { h: 623 },
                    ],
                },
                {
                    w: 882,
                    cells: [
                        // node 75:7175: the photo anchored to its bottom
                        { h: 347, w: 602, phone: false, image: own("1-1", 1800, 1038) },
                        { h: 496, keep: true, image: COVER, clip: { src: "/work/lufthansa/c-cover" } }, // node 75:7176, section1.mp4
                    ],
                },
                {
                    w: 443,
                    cells: [
                        { h: 443 },
                        {
                            h: 400,
                            grow: true,
                            card: {
                                title: "Crafting a highly capable flying experience",
                                // (Tiago, 5 Nov: shorter on phones)
                                phoneTitle: "A new\nflying experience",
                                body: "DEPT® has been re-wiring the way travellers experience comfort and ease throughout their full journey. From dreaming of your destination, to after care, all touch points are being re-imagined, modernised, transported into the far future.",
                            },
                        },
                    ],
                },
            ],
        },
        // 2: portrait 446 x 652 | video 1107 x 846 (phones: the video, a landscape photo under it)
        {
            type: "grid",
            phoneCenter: true,
            columns: [
                { w: 446, cells: [{ h: 652, phone: false, image: own("2-1", 584, 854) }, { h: 191 }] },
                {
                    w: 1107,
                    cells: [
                        { h: 846, keep: 16 / 9, image: SEAT, clip: { src: "/work/lufthansa/c-seat" } }, // node 78:8745, section2.mp4
                        { h: 846, desktop: false, keep: 1246 / 854, image: own("2-p", 1246, 854) }, // node 80:8946
                    ],
                },
            ],
        },
        // 3: video 1107 x 846 | portrait 446 x 652 (phones: the video, a landscape photo under it)
        {
            type: "grid",
            phoneCenter: true,
            columns: [
                {
                    w: 1107,
                    cells: [
                        { h: 846, keep: 16 / 9, image: CHAT, clip: { src: "/work/lufthansa/c-chat" } }, // node 78:8780, section3.mp4
                        { h: 846, desktop: false, keep: 1575 / 1080, image: own("3-p", 1575, 1080) }, // node 80:8948
                    ],
                },
                { w: 446, cells: [{ h: 652, phone: false, image: own("3-1", 739, 1080) }, { h: 191 }] },
            ],
        },
        // 4: two white cards (the assistant's states, 262 x 350 on each, a caption under) | chat flow 666 x 846
        // (phones: the two cards one above the other)
        {
            type: "grid",
            phoneCenter: true,
            columns: [
                {
                    w: 446,
                    cells: [
                        // node 80:8890 "Talking 1" at 92, 151 on card 79:8866; caption 80:8895 (Figma said "Idle"; Tiago, 5 Nov: "Talking")
                        { h: 652, image: TALKING, clip: { src: "/work/lufthansa/c-talking", box: [92, 151, 262, 350, 0], bg: "#ffffff" }, label: { text: "Talking", y: 597 } },
                        { h: 191 },
                    ],
                },
                {
                    w: 443,
                    cells: [
                        // node 80:8891 "Thinking 1" at 91, 151 on card 79:8888; caption 80:8900
                        { h: 652, image: THINKING, clip: { src: "/work/lufthansa/c-thinking", box: [91, 151, 262, 350, 0], bg: "#ffffff" }, label: { text: "Thinking", y: 597 } },
                        { h: 191 },
                    ],
                },
                { w: 666, cells: [{ h: 846, phone: false, image: FLOW_A, clip: { src: "/work/lufthansa/c-flow-a" } }] }, // node 80:8942, Module5_right.mp4
            ],
        },
        // 5: the window video 892 x 652 | chat flow 666 x 846 (phones: the window video only)
        {
            type: "grid",
            phoneCenter: true,
            columns: [
                { w: 892, cells: [{ h: 652, keep: 16 / 9, image: WINDOW, clip: { src: "/work/lufthansa/c-window" } }, { h: 191 }] }, // node 80:8935, Scene-15.mp4
                { w: 666, cells: [{ h: 846, phone: false, image: FLOW_B, clip: { src: "/work/lufthansa/c-flow-b" } }] }, // node 80:8944, Module4_right.mp4
            ],
        },
        // phones only: module 5's chat flow, full width (Tiago, 5 Nov: only this one)
        {
            type: "grid",
            phoneOnly: true,
            phoneCenter: true,
            columns: [{ w: 750, cells: [{ h: 1000, keep: 750 / 1000, image: FLOW_B, clip: { src: "/work/lufthansa/c-flow-b" } }] }],
        },
    ],
}
